import { Avatar, Box, Button, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import { useRef, useState } from 'react';

import { createBrand, removeBrandLogo, updateBrand, uploadBrandLogo } from '../../api/business/endpoints';
import type { Brand, BrandInput } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { ErrorNotice } from '../../components/ErrorNotice';
import { FormDialog } from '../../components/FormDialog';
import { HtmlTextField } from '../../components/HtmlTextField';
import { LanguageTabs } from '../../components/LanguageTabs';
import { languageName, textChanges } from '../../components/localisedText';
import { useContentLanguages } from '../../components/useContentLanguages';
import { useLanguage } from '../../i18n/LanguageContext';

type BrandDialogProps = {
  companyId: string;
  /** null creates a brand; a brand edits it. */
  brand: Brand | null;
  onClose: () => void;
};

const INVALIDATES = ['brands', 'product', 'products'];

/**
 * A brand: its name — the same in every language — a description per content language, whether it is offered,
 * and, once it exists, its logo (R0030 WP-06).
 */
export const BrandDialog = ({ companyId, brand, onClose }: BrandDialogProps) => {
  const { language, t } = useLanguage();
  const { languages, available } = useContentLanguages();
  const original = brand
    ? Object.fromEntries(languages.map((code) => [code, brand.translations[code]?.description ?? '']))
    : null;
  const [name, setName] = useState(brand?.name ?? '');
  const [descriptions, setDescriptions] = useState<Record<string, string>>(original ?? {});
  const [active, setActive] = useState(brand?.active ?? true);
  const [current, setCurrent] = useState<Brand | null>(brand);
  const [tab, setTab] = useState(languages[0] ?? 'sk');
  const file = useRef<HTMLInputElement>(null);
  const code = languages.includes(tab) ? tab : languages[0];

  const save = useAppMutation(
    (input: BrandInput) => (brand ? updateBrand(companyId, brand.id, input) : createBrand(companyId, input)),
    INVALIDATES,
    onClose,
  );
  const upload = useAppMutation(
    (logo: File) => uploadBrandLogo(companyId, brand?.id ?? '', logo, () => undefined),
    INVALIDATES,
    setCurrent,
  );
  const removeLogo = useAppMutation(
    () => removeBrandLogo(companyId, brand?.id ?? ''),
    INVALIDATES,
    setCurrent,
  );

  const submit = (): void => {
    const changes = textChanges(descriptions, original, languages);

    save.mutate({
      ...(brand === null || name.trim() !== brand.name ? { name: name.trim() } : {}),
      translations: Object.fromEntries(
        Object.entries(changes).map(([locale, description]) => [locale, { description }]),
      ),
      active,
    });
  };

  return (
    <FormDialog
      title={brand ? t('brands.edit') : t('brands.create')}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={brand ? t('common.save') : t('common.create')}
      isBusy={save.isPending || name.trim() === ''}
      error={save.error}
    >
      <TextField
        autoFocus
        required
        label={t('brands.name')}
        helperText={t('brands.nameHint')}
        value={name}
        inputProps={{ maxLength: 120 }}
        onChange={(event) => setName(event.target.value)}
      />
      {languages.length > 1 ? (
        <LanguageTabs
          languages={languages}
          value={code}
          onChange={setTab}
          missing={(one) => (descriptions[one] ?? '').trim() === ''}
        />
      ) : null}
      <HtmlTextField
        label={
          languages.length > 1
            ? `${t('brands.description')} (${languageName(code, available, language)})`
            : t('brands.description')
        }
        value={descriptions[code] ?? ''}
        onChange={(value) => setDescriptions({ ...descriptions, [code]: value })}
        maxLength={20000}
        rows={3}
      />
      <FormControlLabel
        control={<Switch checked={active} onChange={(event) => setActive(event.target.checked)} />}
        label={t('brands.active')}
      />
      {current ? (
        <Stack spacing={1}>
          <Typography variant="overline" color="text.secondary">
            {t('brands.logo')}
          </Typography>
          <ErrorNotice error={upload.error ?? removeLogo.error} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              variant="rounded"
              src={current.logo?.urls.small.webp}
              alt={current.logo ? current.name : t('brands.noLogo')}
              sx={{ width: 72, height: 72, bgcolor: 'action.hover', color: 'text.disabled' }}
            >
              {current.name.slice(0, 1)}
            </Avatar>
            <Stack spacing={0.5}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" disabled={upload.isPending} onClick={() => file.current?.click()}>
                  {current.logo ? t('brands.logoReplace') : t('brands.logoUpload')}
                </Button>
                {current.logo ? (
                  <Button
                    size="small"
                    color="warning"
                    disabled={removeLogo.isPending}
                    onClick={() => removeLogo.mutate(undefined)}
                  >
                    {t('brands.logoRemove')}
                  </Button>
                ) : null}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {t('brands.logoHint')}
              </Typography>
            </Stack>
            <input
              ref={file}
              type="file"
              hidden
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              onChange={(event) => {
                const chosen = event.target.files?.[0];

                if (chosen) upload.mutate(chosen);

                event.target.value = '';
              }}
            />
          </Box>
        </Stack>
      ) : null}
    </FormDialog>
  );
};
